import {Group} from '@mantine/core';
import React from 'react';

import {ArchivableItem} from 'archive/ArchiveConstants';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import SettingsPageContainer from 'settings/SettingsPageContainer';

import SchemaSettingsExportDropdown from './SchemaSettingsExportDropdown';

export interface ArchiveSchemaConfig {
  onArchive: (id: string, purpose: string) => void;
  onUnarchive: (id: string) => void;
  isArchivable: boolean;
  isUnarchivable: boolean;
}

interface SchemaSettingsPageContainerProps {
  additionalToolbarItems?: React.ReactNode;
  archiveConfig?: ArchiveSchemaConfig;
  children: React.ReactNode;
  onExportSpreadsheetTemplate?: () => void;
  onNavigateToListing: () => void;
  // Include schema if editing, or null if creating.
  schema: ArchivableItem | null;
  schemaTypeName: string;
  showExport?: boolean;
}

interface SchemaSettingsPageContainerToolbarProps {
  additionalToolbarItems?: React.ReactNode;
  archiveConfig?: ArchiveSchemaConfig;
  onExportSpreadsheetTemplate?: () => void;
  schema: ArchivableItem;
  showExport: boolean;
}

function SchemaSettingsPageContainerToolbar(props: SchemaSettingsPageContainerToolbarProps): JSX.Element {
  return (
    <Group spacing="sm">
      {props.archiveConfig && (
        <ArchiveOrUnarchiveButton
          item={props.schema}
          isArchivable={props.archiveConfig.isArchivable}
          isUnarchivable={props.archiveConfig.isUnarchivable}
          onArchive={(_, purpose) => props.archiveConfig?.onArchive(props.schema.id, purpose)}
          onUnarchive={(_) => props.archiveConfig?.onUnarchive(props.schema.id)}
        />
      )}
      {props.additionalToolbarItems}
      {props.showExport && (
        <SchemaSettingsExportDropdown
          onExportSpreadsheetTemplate={props.onExportSpreadsheetTemplate}
          schemaId={props.schema.id}
        />
      )}
    </Group>
  );
}

/*
 * Container component for a schema page, where a user is either creating or editing a schema.
 */
export default function SchemaSettingsPageContainer(props: SchemaSettingsPageContainerProps): JSX.Element {
  return (
    <SettingsPageContainer
      onClickTitle={props.onNavigateToListing}
      subtitle={
        props.schema ? (
          <span>
            {props.schema.name}
            <ArchiveRecordLabel item={props.schema} />
          </span>
        ) : (
          `Create ${props.schemaTypeName}`
        )
      }
      title={`${props.schemaTypeName}s`}
      toolbar={
        props.schema && (
          <SchemaSettingsPageContainerToolbar
            additionalToolbarItems={props.additionalToolbarItems}
            archiveConfig={props.archiveConfig}
            onExportSpreadsheetTemplate={props.onExportSpreadsheetTemplate}
            schema={props.schema}
            showExport={props.showExport ?? true}
          />
        )
      }
    >
      {props.children}
    </SettingsPageContainer>
  );
}
